import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { getMediaUrl } from '@/utils/mediaUrl';

// Highlight Circle Component
export const HighlightCircle = ({ highlight, onClick, isAdd = false }) => {
  const coverUrl = getMediaUrl(highlight?.cover_url);
  
  return (
    <div 
      className="flex flex-col items-center flex-shrink-0 cursor-pointer group"
      onClick={onClick}
      data-testid={isAdd ? 'add-highlight-btn' : `highlight-${highlight?.id}`}
    >
      <div className="relative">
        {/* Border Ring */}
        <div 
          className={`rounded-full p-[2px] ${isAdd ? 'border-2 border-dashed border-gray-300' : ''}`}
          style={!isAdd ? {
            background: 'linear-gradient(135deg, #FF9933 0%, #FFB347 50%, #FF9933 100%)'
          } : {}}
        >
          <div className="rounded-full p-[2px] bg-white">
            {isAdd ? (
              <div className="w-16 h-16 sm:w-[68px] sm:h-[68px] rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-gray-100 transition-colors">
                <Plus className="w-6 h-6 text-gray-400" />
              </div>
            ) : (
              <div className="w-16 h-16 sm:w-[68px] sm:h-[68px] rounded-full overflow-hidden bg-gray-100">
                {coverUrl ? (
                  <img 
                    src={coverUrl} 
                    alt={highlight.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#FF9933] to-[#FF6600] flex items-center justify-center">
                    <span className="text-white text-xl font-bold">
                      {highlight.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Name */}
      <span className="text-[11px] sm:text-xs mt-2 font-medium text-gray-700 truncate w-16 sm:w-[68px] text-center">
        {isAdd ? 'New' : highlight.name}
      </span>
    </div>
  );
};

// Highlights Row Component for Profile Page
export const HighlightsRow = ({ userId, isOwner, onHighlightClick, onCreateClick }) => {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHighlights();
  }, [userId]);

  const fetchHighlights = async () => {
    try {
      const response = await axios.get(`${API}/highlights/user/${userId}`);
      setHighlights(response.data);
    } catch (error) {
      console.error('Failed to fetch highlights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto py-4 px-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col items-center flex-shrink-0 animate-pulse">
            <div className="w-[68px] h-[68px] rounded-full bg-gray-200" />
            <div className="w-12 h-3 mt-2 rounded bg-gray-200" />
          </div>
        ))}
      </div>
    );
  }

  if (highlights.length === 0 && !isOwner) {
    return null;
  }

  return (
    <div 
      className="flex gap-4 overflow-x-auto py-4 px-2 scrollbar-hide"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      {/* Add New Highlight (Owner only) */}
      {isOwner && (
        <HighlightCircle 
          isAdd={true}
          onClick={onCreateClick}
        />
      )}
      
      {/* Highlight Circles */}
      {highlights.map((highlight) => (
        <HighlightCircle
          key={highlight.id}
          highlight={highlight}
          onClick={() => onHighlightClick(highlight)}
        />
      ))}
      
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

// Create/Edit Highlight Modal
export const CreateHighlightModal = ({ user, onClose, onCreated, editHighlight = null }) => {
  const [name, setName] = useState(editHighlight?.name || '');
  const [stories, setStories] = useState([]);
  const [selectedStoryIds, setSelectedStoryIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isEditing = !!editHighlight;

  useEffect(() => {
    fetchUserStories();
  }, []);

  const fetchUserStories = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/stories/user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStories(response.data.stories || []);
    } catch (error) {
      console.error('Failed to fetch stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStory = (storyId) => {
    setSelectedStoryIds((prev) => 
      prev.includes(storyId) 
        ? prev.filter((id) => id !== storyId)
        : [...prev, storyId]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a highlight name');
      return;
    }

    if (selectedStoryIds.length === 0 && !isEditing) {
      toast.error('Please select at least one story');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      
      if (isEditing) {
        await axios.put(`${API}/highlights/${editHighlight.id}`, {
          name: name.trim(),
          story_ids: selectedStoryIds.length > 0 ? selectedStoryIds : undefined
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Highlight updated!');
      } else {
        await axios.post(`${API}/highlights`, {
          name: name.trim(),
          story_ids: selectedStoryIds
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Highlight created!');
      }
      
      if (onCreated) onCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save highlight');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div 
        className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
        data-testid="create-highlight-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {isEditing ? 'Edit Highlight' : 'New Highlight'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Highlight Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Temple Visits, Mantras"
              maxLength={30}
              data-testid="highlight-name-input"
            />
          </div>

          {/* Story Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Stories
            </label>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#FF9933]" />
              </div>
            ) : stories.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
                <p>No active stories</p>
                <p className="text-xs mt-1">Create stories first to add them to highlights</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {stories.map((story) => {
                  const isSelected = selectedStoryIds.includes(story.id);
                  const mediaUrl = getMediaUrl(story.media_url);
                  
                  return (
                    <div
                      key={story.id}
                      onClick={() => toggleStory(story.id)}
                      className={`relative aspect-[9/16] rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
                        isSelected 
                          ? 'border-[#FF9933] ring-2 ring-[#FF9933]/30' 
                          : 'border-transparent hover:border-gray-200'
                      }`}
                    >
                      {story.media_type === 'image' ? (
                        <img 
                          src={mediaUrl}
                          alt="Story"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={mediaUrl}
                          className="w-full h-full object-cover"
                          muted
                        />
                      )}
                      
                      {/* Selection Indicator */}
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                        isSelected 
                          ? 'bg-[#FF9933]' 
                          : 'bg-black/30 border border-white'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                      
                      {/* Media Type Badge */}
                      <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/50 rounded text-white text-[10px]">
                        {story.media_type === 'video' ? '▶' : '📷'} {story.duration}s
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="w-full bg-gradient-to-r from-[#FF9933] to-[#FF6600] hover:from-[#E68A00] hover:to-[#CC5500] text-white"
            data-testid="save-highlight-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </span>
            ) : (
              isEditing ? 'Update Highlight' : 'Create Highlight'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Highlight Viewer (reuses story viewer structure)
export const HighlightViewer = ({ highlight, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const currentStory = highlight?.stories[currentIndex];
  const totalStories = highlight?.stories.length || 0;

  useEffect(() => {
    if (!currentStory || isPaused || isLoading) return;

    const duration = currentStory.duration * 1000;
    const interval = 50;
    const increment = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          if (currentIndex < totalStories - 1) {
            setCurrentIndex((i) => i + 1);
          } else {
            onClose();
          }
          return 0;
        }
        return prev + increment;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [currentIndex, isPaused, isLoading, currentStory]);

  useEffect(() => {
    setProgress(0);
    setIsLoading(true);
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex((i) => i - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < totalStories - 1) {
        setCurrentIndex((i) => i + 1);
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPaused((p) => !p);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [currentIndex, totalStories, onClose]);

  if (!currentStory) return null;

  const mediaUrl = getMediaUrl(currentStory.media_url);

  return (
    <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center">
      <div className="relative w-full h-full max-w-md mx-auto flex flex-col">
        {/* Progress Bars */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2 pt-3">
          {highlight.stories.map((_, idx) => (
            <div key={idx} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-100"
                style={{
                  width: idx < currentIndex ? '100%' : idx === currentIndex ? `${progress}%` : '0%'
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-6 left-0 right-0 z-20 flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF9933] to-[#FF6600] flex items-center justify-center">
              <span className="text-white font-bold">{highlight.name.charAt(0)}</span>
            </div>
            <p className="text-white font-semibold">{highlight.name}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsPaused((p) => !p)}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              {isPaused ? (
                <svg className="w-5 h-5 text-white" fill="white" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              ) : (
                <svg className="w-5 h-5 text-white" fill="white" viewBox="0 0 24 24">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div 
          className="flex-1 flex items-center justify-center"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const width = rect.width;
            
            if (clickX < width / 3 && currentIndex > 0) {
              setCurrentIndex((i) => i - 1);
            } else if (clickX > (width * 2) / 3 && currentIndex < totalStories - 1) {
              setCurrentIndex((i) => i + 1);
            } else {
              setIsPaused((p) => !p);
            }
          }}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}
          
          {currentStory.media_type === 'image' ? (
            <img
              src={mediaUrl}
              alt="Highlight"
              className="max-w-full max-h-full object-contain"
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
            />
          ) : (
            <video
              src={mediaUrl}
              className="max-w-full max-h-full object-contain"
              autoPlay
              playsInline
              muted
              onLoadedData={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default {
  HighlightCircle,
  HighlightsRow,
  CreateHighlightModal,
  HighlightViewer
};
